/**
 * client/src/features/income/components/IncomeCards/ContractorCard.jsx
 * 
 * Contractor/1099 income card component.
 * Displays contractor income information with self-employment tax calculations.
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
  TrashIcon, 
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentArrowUpIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  CalendarIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate, getFrequencyLabel, normalizeIncomeData } from '../../utils/incomeUtils';
import { calculateTaxSummary, formatTaxSummary } from '../../utils/taxCalculator';
import useIncome from '../../hooks/useIncome';

// US States options for dropdown
const STATE_OPTIONS = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Helper function to extract income ID as string
function extractIncomeId(income) {
  if (!income) return null;
  
  // Handle string IDs
  if (typeof income._id === 'string') {
    return income._id;
  }
  
  // Handle MongoDB ObjectId objects
  if (income._id && typeof income._id === 'object') {
    return income._id.toString ? income._id.toString() : String(income._id);
  }
  
  // Handle nested ID structures
  if (income.id) {
    return typeof income.id === 'string' ? income.id : String(income.id);
  }
  
  console.warn('⚠️ Could not extract income ID from:', income);
  return null;
}

/**
 * ContractorCard Component
 * 
 * Displays details for a contractor/1099 income source.
 * Includes tax estimations, payment schedules, and contract details.
 * 
 * @param {Object} props
 * @param {Object} props.income - Income object with contractor details
 * @param {Function} props.refreshIncomes - Function to refresh income data
 * @param {Function} props.onEdit - Function to handle editing this income
 */
const ContractorCard = ({ income, refreshIncomes, onEdit, className = '' }) => {
  // Log incoming data for debugging
  console.log('📊 Rendering ContractorCard with data:', income);
  console.log('📊 Income type:', income.type || income.category || 'Not specified');
  
  // Normalize income data for display
  const normalizedIncome = normalizeIncomeData(income);
  console.log('📊 Normalized contractor income data:', normalizedIncome);
  
  // Expanded state for detailed view
  const [expanded, setExpanded] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSensitiveData, setShowSensitiveData] = useState(true);
  
  // Edit mode state (inline editing in expanded details)
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  // Get income management functions from custom hook
  const { deleteIncome, uploadPayStub, updateIncome } = useIncome();
  
  // Initialize edit form data
  const initializeEditForm = () => {
    const contractorDetails = income.typeSpecificDetails?.contractor || {};
    const taxDetails = contractorDetails.taxes || {};
    const deductions = contractorDetails.deductions || {};
    
    setEditFormData({
      name: income.name || '',
      employerName: contractorDetails.employer || income.employerName || '',
      grossAmount: income.amount || income.grossAmount || '',
      frequency: income.frequency || 'monthly',
      state: income.location?.state || income.state || contractorDetails.state || contractorDetails.taxes?.state || '',
      startDate: income.startDate ? income.startDate.split('T')[0] : '',
      endDate: income.endDate ? income.endDate.split('T')[0] : '',
      businessStructure: contractorDetails.businessStructure || 'sole_proprietor',
      filingStatus: contractorDetails.filingStatus || 'single',
      contractStatus: (income.endDate && new Date(income.endDate) < new Date()) ? 'ended' : 'active',
      
      // Business expenses - now mapped to individual server schema fields
      homeOfficeExpenses: contractorDetails.expenses?.homeOfficeExpenses || 0,
      equipmentExpenses: contractorDetails.expenses?.equipmentExpenses || 0,
      softwareSubscriptions: contractorDetails.expenses?.softwareSubscriptions || 0,
      professionalDevelopment: contractorDetails.expenses?.professionalDevelopment || 0,
      travelExpenses: contractorDetails.expenses?.travelExpenses || 0,
      marketingExpenses: contractorDetails.expenses?.marketingExpenses || 0,
      internetPhone: contractorDetails.expenses?.internetPhone || 0,
      professionalServices: contractorDetails.expenses?.professionalServices || 0,
      insurance: contractorDetails.expenses?.insurance || 0,
      supplies: contractorDetails.expenses?.supplies || 0,
      
      // Deductions - these map directly to server fields
      healthInsurance: contractorDetails.expenses?.healthInsurance || 0,
      retirementContribution: contractorDetails.expenses?.retirementContribution || 0,
      professionalExpenses: contractorDetails.expenses?.professionalExpenses || 0,
      
      // Tax withholdings (will be calculated)
      federalTax: taxDetails.federalTax || 0,
      stateTax: taxDetails.stateTax || 0,
      selfEmploymentTax: taxDetails.fica || 0,
      totalTax: taxDetails.totalTax || 0,
      netAmount: taxDetails.netIncome || 0
    });
  };
  
  // Handle edit form field changes with automatic tax calculations
  const handleEditFieldChange = (field, value) => {
    const updatedData = { ...editFormData, [field]: value };
    
    // Calculate total business expenses when any business expense field changes
    const businessExpenseFields = [
      'homeOfficeExpenses', 'equipmentExpenses', 'softwareSubscriptions', 
      'professionalDevelopment', 'travelExpenses', 'marketingExpenses', 
      'internetPhone', 'professionalServices', 'insurance', 'supplies'
    ];
    
    let totalBusinessExpenses = 0;
    if (businessExpenseFields.includes(field) || ['grossAmount', 'frequency', 'state', 'filingStatus'].includes(field)) {
      // Calculate total business expenses
      totalBusinessExpenses = businessExpenseFields.reduce((total, expenseField) => {
        return total + (parseFloat(updatedData[expenseField]) || 0);
      }, 0);
    }
    
    // Trigger tax recalculation for relevant fields
    if (['grossAmount', 'frequency', 'state', 'filingStatus', 'healthInsurance', 'retirementContribution', 'professionalExpenses', ...businessExpenseFields].includes(field)) {
      const taxCalculationData = {
        amount: parseFloat(updatedData.grossAmount) || 0,
        grossAmount: parseFloat(updatedData.grossAmount) || 0,
        frequency: updatedData.frequency || 'monthly',
        state: updatedData.state || '',
        filingStatus: updatedData.filingStatus || 'single',
        type: 'CONTRACTOR',
        businessExpenses: totalBusinessExpenses,
        deductions: {
          healthInsurance: parseFloat(updatedData.healthInsurance) || 0,
          retirement: parseFloat(updatedData.retirementContribution) || 0,
          professional: parseFloat(updatedData.professionalExpenses) || 0
        }
      };
      
      console.log('🧮 Calculating taxes for contractor with data:', taxCalculationData);
      const taxSummary = calculateTaxSummary(taxCalculationData);
      console.log('🧮 Tax calculation result:', taxSummary);
      
      // Update form data with calculated values
      updatedData.federalTax = taxSummary.federal || 0;
      updatedData.stateTax = taxSummary.state || 0;
      updatedData.selfEmploymentTax = taxSummary.selfEmployment || 0;
      updatedData.totalTax = taxSummary.total || 0;
      updatedData.netAmount = taxSummary.netAnnual || 0;
    }
    
    setEditFormData(updatedData);
  };
  
  // Handle edit form save
  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      console.log('💾 Saving contractor income with data:', editFormData);
      
      // Extract income and bucket IDs properly
      const incomeId = extractIncomeId(income);
      const bucketId = income.bucketId || income.bucket?._id || income.bucket;
      
      if (!incomeId || !bucketId) {
        console.error('❌ Missing required IDs for save:', { incomeId, bucketId });
        return;
      }
      
      // Prepare update data in expected format (matching server schema exactly)
      const updateData = {
        name: editFormData.name,
        amount: parseFloat(editFormData.grossAmount),
        frequency: (editFormData.frequency || 'monthly').toLowerCase(),
        state: editFormData.state, // Top-level state for location
        startDate: editFormData.startDate,
        endDate: editFormData.endDate || null,
        
        // Ensure location structure is included
        location: {
          state: editFormData.state,
          zipCode: editFormData.zipCode || null
        },
        
        typeSpecificDetails: {
          contractor: {
            employer: editFormData.employerName,
            businessStructure: editFormData.businessStructure,
            filingStatus: editFormData.filingStatus,
            
            taxes: {
              annualGross: parseFloat(editFormData.grossAmount) * (editFormData.frequency === 'monthly' ? 12 : 
                          editFormData.frequency === 'weekly' ? 52 : 
                          editFormData.frequency === 'biweekly' ? 26 : 1),
              federalTax: parseFloat(editFormData.federalTax) || 0,
              stateTax: parseFloat(editFormData.stateTax) || 0,
              fica: parseFloat(editFormData.selfEmploymentTax) || 0,
              totalTax: parseFloat(editFormData.totalTax) || 0,
              netIncome: parseFloat(editFormData.netAmount) || 0,
              effectiveTaxRate: parseFloat(editFormData.effectiveRate) || 0
            },
            
            expenses: {
              // Map form fields to individual server schema fields (no more aggregation needed)
              homeOfficeExpenses: parseFloat(editFormData.homeOfficeExpenses) || 0,
              equipmentExpenses: parseFloat(editFormData.equipmentExpenses) || 0,
              softwareSubscriptions: parseFloat(editFormData.softwareSubscriptions) || 0,
              professionalDevelopment: parseFloat(editFormData.professionalDevelopment) || 0,
              travelExpenses: parseFloat(editFormData.travelExpenses) || 0,
              marketingExpenses: parseFloat(editFormData.marketingExpenses) || 0,
              internetPhone: parseFloat(editFormData.internetPhone) || 0,
              professionalServices: parseFloat(editFormData.professionalServices) || 0,
              insurance: parseFloat(editFormData.insurance) || 0,
              supplies: parseFloat(editFormData.supplies) || 0,
              healthInsurance: parseFloat(editFormData.healthInsurance) || 0,
              retirementContribution: parseFloat(editFormData.retirementContribution) || 0,
              professionalExpenses: parseFloat(editFormData.professionalExpenses) || 0,
              totalBusinessExpenses: (
                (parseFloat(editFormData.homeOfficeExpenses) || 0) +
                (parseFloat(editFormData.equipmentExpenses) || 0) +
                (parseFloat(editFormData.softwareSubscriptions) || 0) +
                (parseFloat(editFormData.professionalDevelopment) || 0) +
                (parseFloat(editFormData.travelExpenses) || 0) +
                (parseFloat(editFormData.marketingExpenses) || 0) +
                (parseFloat(editFormData.internetPhone) || 0) +
                (parseFloat(editFormData.professionalServices) || 0) +
                (parseFloat(editFormData.insurance) || 0) +
                (parseFloat(editFormData.supplies) || 0) +
                (parseFloat(editFormData.healthInsurance) || 0) +
                (parseFloat(editFormData.retirementContribution) || 0) +
                (parseFloat(editFormData.professionalExpenses) || 0)
              )
            }
          }
        }
      };
      
      console.log('💾 Formatted update data:', updateData);
      
      // Call useIncome hook with correct parameters: (bucketId, incomeId, incomeData)
      await updateIncome(bucketId, incomeId, updateData);
      
      setIsEditMode(false);
      
      // Add small delay to ensure server response is processed before refresh
      setTimeout(() => {
        refreshIncomes();
      }, 100);
      
      console.log('✅ Contractor income updated successfully');
    } catch (error) {
      console.error('❌ Error saving contractor income:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Access all data from typeSpecificDetails.contractor
  const contractorDetails = income.typeSpecificDetails?.contractor || {};
  
  // Basic details
  const employerName = contractorDetails.employer || income.name || 'Not specified';
  const filingStatus = contractorDetails.filingStatus || 'single';
  const businessStructure = contractorDetails.businessStructure || 'sole_proprietor';
  
  // Get state from multiple possible locations - check location.state first since that's where saves go
  const state = income.location?.state ||
                income.state || 
                contractorDetails.state || 
                contractorDetails.taxes?.state || 
                'Not specified';
  
  console.log('📊 Using state:', state);
  
  // Business expenses
  const businessExpenses = contractorDetails.expenses || {
    homeOfficeSqft: 0,
    totalHomeSqft: 0,
    monthlyHomeExpenses: 0,
    equipmentExpenses: 0,
    professionalServices: 0,
    travelExpenses: 0,
    marketingExpenses: 0,
    healthInsurance: 0,
    otherExpenses: 0,
    homeOfficeDeduction: 0,
    totalBusinessExpenses: 0
  };
  
  // Tax details - calculate if not present
  let taxDetails = contractorDetails.taxes || {};
  
  // If no tax data exists, calculate it
  if (!taxDetails.federalTax && !taxDetails.stateTax && !taxDetails.fica && income.amount > 0) {
    const grossAmount = parseFloat(income.amount || 0);
    const frequency = income.frequency || 'monthly';
    const state = income.location?.state || income.state || contractorDetails.state || '';
    const filingStatus = contractorDetails.filingStatus || 'single';
    
    // Calculate total business expenses
    const businessExpenseFields = [
      'homeOffice', 'equipment', 'software', 'training', 'travel', 
      'marketing', 'utilities', 'professional', 'insurance', 'supplies'
    ];
    
    const totalBusinessExpenses = businessExpenseFields.reduce((total, field) => {
      return total + (parseFloat(contractorDetails.expenses?.[field]) || 0);
    }, 0);
    
    const deductions = contractorDetails.deductions || {};
    
    const taxCalculationData = {
      amount: grossAmount,
      grossAmount: grossAmount,
      frequency: frequency,
      state: state,
      filingStatus: filingStatus,
      type: 'CONTRACTOR',
      businessExpenses: totalBusinessExpenses,
      deductions: {
        healthInsurance: parseFloat(deductions.healthInsurance) || 0,
        retirement: parseFloat(deductions.retirement) || 0,
        professional: parseFloat(deductions.professional) || 0
      }
    };
    
    console.log('🧮 Auto-calculating missing tax data for contractor:', taxCalculationData);
    const taxSummary = calculateTaxSummary(taxCalculationData);
    console.log('🧮 Auto-calculated tax result:', taxSummary);
    
    taxDetails = {
      annualGross: grossAmount * (frequency === 'monthly' ? 12 : frequency === 'weekly' ? 52 : frequency === 'biweekly' ? 26 : 1),
      federalTax: taxSummary.federal || 0,
      stateTax: taxSummary.state || 0,
      fica: taxSummary.selfEmployment || 0,
      totalTax: taxSummary.total || 0,
      netIncome: taxSummary.netAnnual || 0,
      effectiveTaxRate: typeof taxSummary.effectiveRate === 'string' ? parseFloat(taxSummary.effectiveRate) : (taxSummary.effectiveRate || 0)
    };
  }
  
  // Ensure default values
  taxDetails = {
    annualGross: taxDetails.annualGross || 0,
    taxableIncome: taxDetails.taxableIncome || 0,
    federalTax: taxDetails.federalTax || 0,
    stateTax: taxDetails.stateTax || 0,
    fica: taxDetails.fica || 0,
    totalTax: taxDetails.totalTax || 0,
    netIncome: taxDetails.netIncome || 0,
    effectiveTaxRate: taxDetails.effectiveTaxRate || 0
  };
  
  // Fix effective tax rate to show 2 decimal places
  const effectiveRateFormatted = (taxDetails.effectiveTaxRate || 0).toFixed(2) + '%';
  
  // Tax calculations
  const formattedTaxSummary = {
    selfEmployment: taxDetails.fica || 0,
    federal: taxDetails.federalTax || 0,
    state: taxDetails.stateTax || 0,
    total: taxDetails.totalTax || 0,
    effectiveRate: typeof taxDetails.effectiveTaxRate === 'string' 
      ? parseFloat(taxDetails.effectiveTaxRate) 
      : (taxDetails.effectiveTaxRate || 0)
  };

  // Calculate effective rate if it's missing or zero (fallback calculation)
  console.log('🧮 Effective rate debug:', {
    effectiveRate: formattedTaxSummary.effectiveRate,
    total: formattedTaxSummary.total,
    annualGross: taxDetails.annualGross,
    shouldCalculateFallback: !formattedTaxSummary.effectiveRate && formattedTaxSummary.total > 0 && taxDetails.annualGross > 0
  });
  
  if (!formattedTaxSummary.effectiveRate && formattedTaxSummary.total > 0 && taxDetails.annualGross > 0) {
    formattedTaxSummary.effectiveRate = (formattedTaxSummary.total / taxDetails.annualGross) * 100;
    console.log('🧮 Calculated fallback effective rate:', formattedTaxSummary.effectiveRate.toFixed(2) + '%');
  }
  
  // Format amounts for display
  const grossAmount = income.amount || 0;
  const monthlyTotalTax = formattedTaxSummary.total / 12;
  const monthlyNetAmount = grossAmount - monthlyTotalTax;
  const annualNetAmount = (grossAmount * 12) - formattedTaxSummary.total;
  
  const formattedGrossAmount = formatCurrency(grossAmount);
  const formattedNetAmount = formatCurrency(monthlyNetAmount);
  
  const quarterlyTaxEstimate = Math.ceil(formattedTaxSummary.total / 4);
  
  // Dates and frequency
  const startDate = income.startDate ? new Date(income.startDate) : new Date();
  const endDate = income.endDate ? new Date(income.endDate) : null;
  const frequencyLabel = getFrequencyLabel(income.frequency);
  
  // Contract details
  const contractType = 'Contract';
  const contractTerms = '';
  const is1099 = true;
  
  console.log('📊 BUSINESS EXPENSES:', JSON.stringify(businessExpenses, null, 2));
  
  // Calculate home office deduction if not already calculated
  if (!businessExpenses.homeOfficeDeduction && businessExpenses.homeOfficeSqft > 0 && businessExpenses.totalHomeSqft > 0) {
    const percentage = (businessExpenses.homeOfficeSqft / businessExpenses.totalHomeSqft) * 100;
    businessExpenses.homeOfficeDeduction = (businessExpenses.monthlyHomeExpenses * percentage) / 100;
  }
  
  // Calculate total business expenses if not already calculated
  if (!businessExpenses.totalBusinessExpenses) {
    businessExpenses.totalBusinessExpenses = (
      businessExpenses.homeOfficeDeduction +
      businessExpenses.equipmentExpenses +
      businessExpenses.professionalServices +
      businessExpenses.travelExpenses +
      businessExpenses.marketingExpenses +
      businessExpenses.healthInsurance +
      businessExpenses.otherExpenses
    );
  }
  
  // Access expense information
  const estimatedExpenses = 
    income.typeSpecificDetails?.contractor?.expenses?.estimatedExpenses || 
    income.estimatedExpenses || 
    businessExpenses.totalBusinessExpenses || 
    0;
    
  const quarterlyTaxRate = 
    income.typeSpecificDetails?.contractor?.expenses?.quarterlyTaxRate || 
    income.quarterlyTaxRate || 
    0;
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      console.log('📄 Uploading document:', file.name);
      await uploadPayStub(income._id, file);
      refreshIncomes();
    } catch (err) {
      console.error('❌ Error uploading document:', err);
    }
  };
  
  // Handle income deletion
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const bucketId = income.bucketId || (income.bucket?._id) || income.bucket;
      
      if (!bucketId) {
        console.error('❌ Cannot delete income: missing bucketId');
        return;
      }
      
      console.log(`🗑️ Deleting income ${income._id} from bucket ${bucketId}...`);
      await deleteIncome(bucketId, income._id);
      setShowDeleteModal(false);
      refreshIncomes();
    } catch (err) {
      console.error('❌ Error deleting income:', err);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Toggle expanded view
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  
  // Handle edit button click
  const handleEditClick = () => {
    initializeEditForm();
    setIsEditMode(true);
    setExpanded(true); // Auto-expand details when editing
    
    // Trigger initial tax calculation after form initialization
    setTimeout(() => {
      const contractorDetails = income.typeSpecificDetails?.contractor || {};
      const expenseData = contractorDetails.expenses || {};
      
      const grossAmount = parseFloat(income.amount || income.grossAmount || 0);
      const frequency = income.frequency || 'monthly';
      const state = income.location?.state || income.state || contractorDetails.state || contractorDetails.taxes?.state || '';
      const filingStatus = contractorDetails.filingStatus || 'single';
      
      // Calculate total business expenses using correct server field names
             const totalBusinessExpenses = (
         (parseFloat(expenseData.homeOfficeExpenses) || 0) +
         (parseFloat(expenseData.equipmentExpenses) || 0) +
         (parseFloat(expenseData.softwareSubscriptions) || 0) +
         (parseFloat(expenseData.professionalDevelopment) || 0) +
         (parseFloat(expenseData.travelExpenses) || 0) +
         (parseFloat(expenseData.marketingExpenses) || 0) +
         (parseFloat(expenseData.internetPhone) || 0) +
         (parseFloat(expenseData.professionalServices) || 0) +
         (parseFloat(expenseData.insurance) || 0) +
         (parseFloat(expenseData.supplies) || 0) +
         (parseFloat(expenseData.healthInsurance) || 0) +
         (parseFloat(expenseData.retirementContribution) || 0) +
         (parseFloat(expenseData.professionalExpenses) || 0)
       );
      
      // Perform tax calculation if we have sufficient data
      if (grossAmount > 0) {
        const taxCalculationData = {
          amount: grossAmount,
          grossAmount: grossAmount,
          frequency: frequency,
          state: state,
          filingStatus: filingStatus,
          type: 'CONTRACTOR',
          businessExpenses: totalBusinessExpenses,
          deductions: {
            healthInsurance: parseFloat(expenseData.healthInsurance) || 0,
            retirement: parseFloat(expenseData.retirementContribution) || 0,
            professional: parseFloat(expenseData.professionalExpenses) || 0
          }
        };
        
        console.log('🧮 Initial tax calculation for contractor with data:', taxCalculationData);
        const taxSummary = calculateTaxSummary(taxCalculationData);
        console.log('🧮 Initial tax calculation result:', taxSummary);
        
        // Update form data with calculated values
        setEditFormData(prev => ({
          ...prev,
          federalTax: taxSummary.federal || 0,
          stateTax: taxSummary.state || 0,
          selfEmploymentTax: taxSummary.selfEmployment || 0,
          totalTax: taxSummary.total || 0,
          netAmount: taxSummary.netAnnual || 0
        }));
      }
    }, 100); // Small delay to ensure form data is set
  };
  
  // Handle edit cancel
  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
  };
  
  // Format next payment date if available
  const nextPaymentDate = income.nextPaymentDate 
    ? formatDate(income.nextPaymentDate, 'medium')
    : 'Not scheduled';

  // Helper function to convert annual amounts to the selected frequency
  const convertToFrequency = (annualAmount, frequency) => {
    const freq = frequency.toLowerCase();
    switch (freq) {
      case 'weekly': return annualAmount / 52;
      case 'biweekly': return annualAmount / 26;
      case 'monthly': return annualAmount / 12;
      case 'quarterly': return annualAmount / 4;
      case 'annually': case 'annual': return annualAmount;
      default: return annualAmount / 12; // Default to monthly
    }
  };

  // Helper function to get frequency label for display
  const getFrequencyDisplayLabel = (frequency) => {
    const freq = frequency.toLowerCase();
    switch (freq) {
      case 'weekly': return 'Weekly';
      case 'biweekly': return 'Bi-weekly';
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Quarterly';
      case 'annually': case 'annual': return 'Annual';
      default: return 'Monthly';
    }
  };

  // Get current frequency for calculations (from edit mode or income data)
  const currentFrequency = isEditMode ? editFormData.frequency : income.frequency;
  const currentFrequencyLabel = getFrequencyDisplayLabel(currentFrequency || 'monthly');
    
  // Get business structure label
  const getBusinessStructureLabel = (structure) => {
    switch (structure) {
      case 'SOLE_PROP': return 'Sole Proprietorship';
      case 'LLC': return 'LLC';
      case 'S_CORP': return 'S-Corporation';
      case 'C_CORP': return 'C-Corporation';
      case 'PARTNERSHIP': return 'Partnership';
      default: return structure;
    }
  };
  
  // Format dates
  const formattedStartDate = income.startDate ? new Date(income.startDate).toLocaleDateString('en-US', { 
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'Not specified';
  
  const formattedEndDate = income.endDate ? new Date(income.endDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : 'Ongoing';
  
  // Calculate monthly and annual equivalents
  const calculateMonthlyAmount = (amount, frequency) => {
    if (!amount) return 0;
    
    switch (frequency) {
      case 'WEEKLY':
        return amount * 52 / 12;
      case 'BI_WEEKLY':
        return amount * 26 / 12;
      case 'MONTHLY':
        return amount;
      case 'QUARTERLY':
        return amount / 3;
      case 'ANNUALLY':
        return amount / 12;
      default:
        return amount;
    }
  };

  const calculateAnnualAmount = (amount, frequency) => {
    if (!amount) return 0;
    
    switch (frequency) {
      case 'WEEKLY':
        return amount * 52;
      case 'BI_WEEKLY':
        return amount * 26;
      case 'MONTHLY':
        return amount * 12;
      case 'QUARTERLY':
        return amount * 4;
      case 'ANNUALLY':
        return amount;
      default:
        return amount * 12;
    }
  };
  
  // Monthly and annual calculations
  const monthlyGross = calculateMonthlyAmount(normalizedIncome.grossAmount, normalizedIncome.frequency);
  const annualGross = calculateAnnualAmount(normalizedIncome.grossAmount, normalizedIncome.frequency);
  const monthlyNet = calculateMonthlyAmount(normalizedIncome.netAmount || monthlyNetAmount, normalizedIncome.frequency);
  const annualNet = calculateAnnualAmount(normalizedIncome.netAmount || monthlyNetAmount, normalizedIncome.frequency);
  
  // Render expanded details with edit functionality
  const renderExpandedDetails = () => {
    if (!expanded) return null;
    
    return (
      <div className="p-4 bg-gray-50 border-t border-gray-200">
        {/* Contract Details Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Contract Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Income Name</p>
              {isEditMode ? (
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => handleEditFieldChange('name', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter income name"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{income.name}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Client/Employer</p>
              {isEditMode ? (
                <input
                  type="text"
                  value={editFormData.employerName || ''}
                  onChange={(e) => handleEditFieldChange('employerName', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter client/employer name"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{employerName}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Business Structure</p>
              {isEditMode ? (
                <select
                  value={editFormData.businessStructure || 'sole_proprietor'}
                  onChange={(e) => handleEditFieldChange('businessStructure', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="sole_proprietor">Sole Proprietorship</option>
                  <option value="llc">LLC</option>
                  <option value="s_corp">S-Corporation</option>
                  <option value="c_corp">C-Corporation</option>
                  <option value="partnership">Partnership</option>
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">{getBusinessStructureLabel(businessStructure)}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Location (State)</p>
              {isEditMode ? (
                <select
                  value={editFormData.state || ''}
                  onChange={(e) => handleEditFieldChange('state', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {STATE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">{state}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Start Date</p>
              {isEditMode ? (
                <input
                  type="date"
                  value={editFormData.startDate || ''}
                  onChange={(e) => handleEditFieldChange('startDate', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{formattedStartDate}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">End Date (Optional)</p>
              {isEditMode ? (
                <input
                  type="date"
                  value={editFormData.endDate || ''}
                  onChange={(e) => handleEditFieldChange('endDate', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {endDate ? formattedEndDate : 'Ongoing'}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Filing Status</p>
              {isEditMode ? (
                <select
                  value={editFormData.filingStatus || 'single'}
                  onChange={(e) => handleEditFieldChange('filingStatus', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="single">Single</option>
                  <option value="married_joint">Married Filing Jointly</option>
                  <option value="married_separate">Married Filing Separately</option>
                  <option value="head_household">Head of Household</option>
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {filingStatus === 'married_joint' ? 'Married Filing Jointly' :
                   filingStatus === 'married_separate' ? 'Married Filing Separately' :
                   filingStatus === 'head_household' ? 'Head of Household' :
                   filingStatus === 'single' ? 'Single' :
                   filingStatus?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Single'}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Contract Status</p>
              {isEditMode ? (
                <select
                  value={editFormData.contractStatus || 'active'}
                  onChange={(e) => handleEditFieldChange('contractStatus', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="ended">Ended</option>
                  <option value="paused">Paused</option>
                  <option value="pending">Pending</option>
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">
                  {endDate && new Date(endDate) < new Date() ? 'Ended' : 'Active'}
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Payment Details Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Payment Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Payment Frequency</p>
              {isEditMode ? (
                <select
                  value={editFormData.frequency || 'monthly'}
                  onChange={(e) => handleEditFieldChange('frequency', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-900">{frequencyLabel}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Gross Amount</p>
              {isEditMode ? (
                <input
                  type="number"
                  value={editFormData.grossAmount || ''}
                  onChange={(e) => handleEditFieldChange('grossAmount', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              ) : (
                <p className="text-sm font-medium text-gray-900">{formattedGrossAmount}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Est Annual Gross</p>
              <p className="text-sm font-medium text-gray-900 bg-blue-50 px-2 py-1 rounded">
                {formatCurrency(isEditMode ? 
                  (parseFloat(editFormData.grossAmount) || 0) * (editFormData.frequency === 'monthly' ? 12 : 
                    editFormData.frequency === 'weekly' ? 52 : 
                    editFormData.frequency === 'biweekly' ? 26 : 1) :
                  (taxDetails.annualGross || grossAmount * 12)
                )}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Est. Annual Net</p>
              <p className="text-sm font-medium text-gray-900 bg-green-50 px-2 py-1 rounded">
                {formatCurrency(isEditMode ? (editFormData.netAmount || 0) : (taxDetails.netIncome || annualNetAmount))}
              </p>
            </div>
          </div>
        </div>
        
        {/* Business Expenses and Deductions Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Business Expenses and Deductions ({currentFrequencyLabel})</h3>
          {isEditMode ? (
            <div className="grid grid-cols-2 gap-4">
              {/* Business Expenses */}
              <div>
                <p className="text-xs text-gray-600 mb-1">Home Office Expenses ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.homeOfficeExpenses || ''}
                  onChange={(e) => handleEditFieldChange('homeOfficeExpenses', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Equipment & Tools ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.equipmentExpenses || ''}
                  onChange={(e) => handleEditFieldChange('equipmentExpenses', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Software & Subscriptions ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.softwareSubscriptions || ''}
                  onChange={(e) => handleEditFieldChange('softwareSubscriptions', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Professional Development ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.professionalDevelopment || ''}
                  onChange={(e) => handleEditFieldChange('professionalDevelopment', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Travel Expenses ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.travelExpenses || ''}
                  onChange={(e) => handleEditFieldChange('travelExpenses', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Marketing & Advertising ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.marketingExpenses || ''}
                  onChange={(e) => handleEditFieldChange('marketingExpenses', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Internet & Phone ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.internetPhone || ''}
                  onChange={(e) => handleEditFieldChange('internetPhone', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Professional Services ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.professionalServices || ''}
                  onChange={(e) => handleEditFieldChange('professionalServices', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Business Insurance ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.insurance || ''}
                  onChange={(e) => handleEditFieldChange('insurance', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Supplies & Materials ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.supplies || ''}
                  onChange={(e) => handleEditFieldChange('supplies', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              
              {/* Deductions */}
              <div>
                <p className="text-xs text-gray-600 mb-1">Health Insurance ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.healthInsurance || ''}
                  onChange={(e) => handleEditFieldChange('healthInsurance', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Retirement Contribution ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.retirementContribution || ''}
                  onChange={(e) => handleEditFieldChange('retirementContribution', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Professional Expenses ({currentFrequencyLabel})</p>
                <input
                  type="number"
                  value={editFormData.professionalExpenses || ''}
                  onChange={(e) => handleEditFieldChange('professionalExpenses', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-600 mb-1">Total Business Expenses and Deductions ({currentFrequencyLabel})</p>
                <div className="w-full px-2 py-1 text-sm bg-blue-50 border border-blue-200 rounded text-gray-700 font-semibold">
                  {formatCurrency(
                    (parseFloat(editFormData.homeOfficeExpenses) || 0) +
                    (parseFloat(editFormData.equipmentExpenses) || 0) +
                    (parseFloat(editFormData.softwareSubscriptions) || 0) +
                    (parseFloat(editFormData.professionalDevelopment) || 0) +
                    (parseFloat(editFormData.travelExpenses) || 0) +
                    (parseFloat(editFormData.marketingExpenses) || 0) +
                    (parseFloat(editFormData.internetPhone) || 0) +
                    (parseFloat(editFormData.professionalServices) || 0) +
                    (parseFloat(editFormData.insurance) || 0) +
                    (parseFloat(editFormData.supplies) || 0) +
                    (parseFloat(editFormData.healthInsurance) || 0) +
                    (parseFloat(editFormData.retirementContribution) || 0) +
                    (parseFloat(editFormData.professionalExpenses) || 0)
                  )}
                </div>
              </div>
            </div>
          ) : (
            businessExpenses && Object.values(businessExpenses).some(expense => expense > 0) && (
              <div className="grid grid-cols-2 gap-2">
                {businessExpenses.homeOfficeDeduction > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Home Office Deduction</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.homeOfficeDeduction)}</p>
                  </div>
                )}
                {businessExpenses.equipmentExpenses > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Equipment</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.equipmentExpenses)}</p>
                  </div>
                )}
                {businessExpenses.professionalServices > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Professional Services</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.professionalServices)}</p>
                  </div>
                )}
                {businessExpenses.travelExpenses > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Travel</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.travelExpenses)}</p>
                  </div>
                )}
                {businessExpenses.marketingExpenses > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Marketing</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.marketingExpenses)}</p>
                  </div>
                )}
                {businessExpenses.healthInsurance > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Health Insurance</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.healthInsurance)}</p>
                  </div>
                )}
                {businessExpenses.otherExpenses > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Other Expenses</p>
                    <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.otherExpenses)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-600">Total Business Expenses</p>
                  <p className="text-sm font-medium text-gray-900">{formatCurrency(businessExpenses.totalBusinessExpenses)}</p>
                </div>
              </div>
            )
          )}
        </div>
        
        {/* Tax Information Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Tax Information ({currentFrequencyLabel})</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-600 mb-1">Federal Income Tax</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700">
                {formatCurrency(convertToFrequency(
                  isEditMode ? (editFormData.federalTax || 0) : formattedTaxSummary.federal,
                  currentFrequency || 'monthly'
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">State Income Tax</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700">
                {formatCurrency(convertToFrequency(
                  isEditMode ? (editFormData.stateTax || 0) : formattedTaxSummary.state,
                  currentFrequency || 'monthly'
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Self-Employment Tax</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700">
                {formatCurrency(convertToFrequency(
                  isEditMode ? (editFormData.selfEmploymentTax || 0) : formattedTaxSummary.selfEmployment,
                  currentFrequency || 'monthly'
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Total Tax Burden</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700 font-semibold">
                {formatCurrency(convertToFrequency(
                  isEditMode ? (editFormData.totalTax || 0) : formattedTaxSummary.total,
                  currentFrequency || 'monthly'
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Effective Tax Rate</p>
              <div className="w-full px-2 py-1 text-sm bg-gray-50 border border-gray-200 rounded text-gray-700">
                {isEditMode ? 
                  `${((editFormData.totalTax || 0) / Math.max((parseFloat(editFormData.grossAmount) || 1) * (editFormData.frequency === 'monthly' ? 12 : editFormData.frequency === 'weekly' ? 52 : editFormData.frequency === 'biweekly' ? 26 : 1), 1) * 100).toFixed(1)}%` :
                  `${formattedTaxSummary.effectiveRate.toFixed(1)}%`
                }
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">{currentFrequencyLabel} Net Income</p>
              <div className="w-full px-2 py-1 text-sm bg-green-50 border border-green-200 rounded text-gray-700 font-semibold">
                {formatCurrency(convertToFrequency(
                  isEditMode ? (editFormData.netAmount || 0) : (taxDetails.netIncome || annualNetAmount),
                  currentFrequency || 'monthly'
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Estimated Tax Breakdown Section - W2 Style */}
        <div className="mb-4">
          <div className="border-l-4 border-blue-400 bg-blue-50 p-4 rounded-r-lg">
            <h4 className="text-sm font-semibold text-blue-800 mb-3">Estimated Tax Breakdown</h4>
            
            {/* Desktop view - Clean and organized table layout */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-blue-200">
                    <th className="text-left py-1 font-medium text-blue-800">Tax Type</th>
                    <th className="text-right py-1 font-medium text-blue-800">Quarterly</th>
                    <th className="text-right py-1 font-medium text-blue-800">Annual</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-blue-100">
                    <td className="py-2">
                      <div className="text-blue-700">Federal Income Tax</div>
                      <div className="text-blue-600 text-xs">Estimated quarterly payment</div>
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">
                      {formatCurrency(isEditMode ? ((editFormData.federalTax || 0) / 4) : (formattedTaxSummary.federal / 4))}
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">
                      {formatCurrency(isEditMode ? (editFormData.federalTax || 0) : formattedTaxSummary.federal)}
                    </td>
                  </tr>
                  <tr className="border-b border-blue-100">
                    <td className="py-2">
                      <div className="text-blue-700">State Income Tax</div>
                      <div className="text-blue-600 text-xs">State estimated payment</div>
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">
                      {formatCurrency(isEditMode ? ((editFormData.stateTax || 0) / 4) : (formattedTaxSummary.state / 4))}
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">
                      {formatCurrency(isEditMode ? (editFormData.stateTax || 0) : formattedTaxSummary.state)}
                    </td>
                  </tr>
                  <tr className="border-b border-blue-100">
                    <td className="py-2">
                      <div className="text-blue-700">Self-Employment Tax</div>
                      <div className="text-blue-600 text-xs">Social Security & Medicare</div>
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">
                      {formatCurrency(isEditMode ? ((editFormData.selfEmploymentTax || 0) / 4) : (formattedTaxSummary.selfEmployment / 4))}
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">
                      {formatCurrency(isEditMode ? (editFormData.selfEmploymentTax || 0) : formattedTaxSummary.selfEmployment)}
                    </td>
                  </tr>
                  <tr className="border-t-2 border-blue-300 bg-blue-100">
                    <td className="py-2 font-semibold">
                      <div className="text-blue-800">Total Estimated Taxes</div>
                      <div className="text-blue-600 text-xs">Combined quarterly payment</div>
                    </td>
                    <td className="py-2 text-blue-800 font-bold text-right">
                      {formatCurrency(isEditMode ? ((editFormData.totalTax || 0) / 4) : (formattedTaxSummary.total / 4))}
                    </td>
                    <td className="py-2 text-blue-800 font-bold text-right">
                      {formatCurrency(isEditMode ? (editFormData.totalTax || 0) : formattedTaxSummary.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* Mobile view - Simplified layout */}
            <div className="md:hidden space-y-4">
              <div>
                <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                  Quarterly Payments:
                </h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-blue-700">Federal Income Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? ((editFormData.federalTax || 0) / 4) : (formattedTaxSummary.federal / 4))}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">State Income Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? ((editFormData.stateTax || 0) / 4) : (formattedTaxSummary.state / 4))}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">Self-Employment Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? ((editFormData.selfEmploymentTax || 0) / 4) : (formattedTaxSummary.selfEmployment / 4))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div>
                <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                  Annual:
                </h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-blue-700">Federal Income Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? (editFormData.federalTax || 0) : formattedTaxSummary.federal)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">State Income Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? (editFormData.stateTax || 0) : formattedTaxSummary.state)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">Self-Employment Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? (editFormData.selfEmploymentTax || 0) : formattedTaxSummary.selfEmployment)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div>
                <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                  Summary:
                </h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-blue-800 font-medium">Total Tax Payments:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? ((editFormData.totalTax || 0) / 4) : (formattedTaxSummary.total / 4))} / {formatCurrency(isEditMode ? (editFormData.totalTax || 0) : formattedTaxSummary.total)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">Estimated Net Income:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">
                        {formatCurrency(isEditMode ? (editFormData.netAmount || 0) : (taxDetails.netIncome || annualNetAmount))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <p className="text-xs text-blue-600 mt-2">
              This is an estimate based on the information provided. Your actual tax obligations may vary.
            </p>
          </div>
        </div>
        
        {/* Additional Notes Section (if any) */}
        {income.notes && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
            <p className="text-sm text-gray-900">{income.notes}</p>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className={`relative bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 ${className}`}>
      {/* Header - Matching W2/Salary card style */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
            <DocumentTextIcon className="h-5 w-5" />
          </div>
          <h3 className="font-medium text-gray-900">{normalizedIncome.name}</h3>
        </div>

      </div>



      {/* Content */}
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Payment Information - Mobile Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Gross Pay</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(normalizedIncome.grossAmount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {frequencyLabel.toLowerCase()}
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Gross Annual</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(annualGross)}
            </div>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Est. Net Monthly</div>
            <div className="text-base sm:text-lg font-semibold text-blue-600">
              {formatCurrency(monthlyNet)}
            </div>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Est. Net Annual</div>
            <div className="text-base sm:text-lg font-semibold text-blue-600">
              {formatCurrency(annualNet)}
            </div>
          </div>
        </div>

        {/* Expand/Collapse Toggle with Edit/Delete Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-2 border-t border-gray-200 gap-2 sm:gap-0">
          <button
            onClick={toggleExpanded}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <span>{expanded ? 'Hide Details' : 'Show Details'}</span>
            {expanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>
          
          {/* Edit/Delete/Save/Cancel Actions in Toggle Section */}
          <div className="flex items-center space-x-2">
            {isEditMode ? (
              <>
                <button
                  onClick={handleEditSave}
                  disabled={isSaving}
                  className="flex items-center space-x-1 px-3 py-2 bg-green-50 text-green-600 hover:text-green-800 rounded hover:bg-green-100 transition-colors text-xs disabled:opacity-50 min-h-[36px]"
                  aria-label="Save changes"
                  title="Save changes"
                >
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </button>
                <button
                  onClick={handleEditCancel}
                  className="flex items-center space-x-1 px-3 py-2 bg-gray-50 text-gray-600 hover:text-gray-800 rounded hover:bg-gray-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Cancel editing"
                  title="Cancel editing"
                >
                  <span>Cancel</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleEditClick}
                  className="flex items-center space-x-1 px-3 py-2 bg-blue-50 text-blue-600 hover:text-blue-800 rounded hover:bg-blue-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Edit income"
                  title="Edit income"
                >
                  <PencilIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center space-x-1 px-3 py-2 bg-red-50 text-red-500 hover:text-red-700 rounded hover:bg-red-100 transition-colors text-xs min-h-[36px]"
                  aria-label="Delete income"
                  title="Delete income"
                >
                  <TrashIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </>
            )}
            <div className="text-xs text-gray-500 hidden sm:block">
              Started {formattedStartDate}
            </div>
          </div>
        </div>
        
        {/* Expanded details */}
        {renderExpandedDetails()}
      </div>
      
      {/* Document Upload Section */}
      {showDocumentUpload && (
        <div className="p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex flex-col space-y-3">
            <h4 className="text-sm font-medium text-blue-700">Upload 1099 or Contract</h4>
            <input 
              type="file" 
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:p-2 file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <p className="text-xs text-blue-600">
              Supported formats: PDF, JPG, PNG
            </p>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center text-red-600 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
              <h3 className="text-lg font-semibold">Confirm Deletion</h3>
            </div>
            
            <p className="mb-4 text-gray-600">
              Are you sure you want to delete <span className="font-semibold">{normalizedIncome.name}</span>? 
              This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 rounded border border-red-200 transition-colors"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorCard; 